import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Loader2, AlertCircle, Monitor } from "lucide-react";
import type { SharedPreviewResponse } from "@shared-core/data/feature-session-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { currentTime } from "@shared-core/domain/current-time";

export function SharedPreviewPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const dataAdapter = useDataAdapter();

  const [preview, setPreview] = useState<SharedPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!shareToken) return;
      setLoading(true);
      try {
        const result = await dataAdapter.getSharedPreview(shareToken);
        setPreview(result);

        // Check expiry
        if (result.expires_at && new Date(result.expires_at) < currentTime()) {
          setError("This preview has expired.");
          setPreview(null);
        }
      } catch {
        setError("Preview not found or has expired.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dataAdapter, shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="size-6 animate-spin mr-2" />
          Loading preview...
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
          <AlertCircle className="size-10 opacity-40" />
          <p className="text-lg">{error ?? "Preview not available"}</p>
        </div>
      </div>
    );
  }

  if (!preview.preview_url || !preview.preview_healthy) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header
          branchName={preview.branch_name}
          expiresAt={preview.expires_at}
        />
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
          <Monitor className="size-10 opacity-40" />
          <p className="text-lg">Preview is not currently available</p>
          <p className="text-sm">The development session may have ended.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header branchName={preview.branch_name} expiresAt={preview.expires_at} />
      <iframe
        src={preview.preview_url}
        className="flex-1 w-full border-0"
        title="Shared Preview"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
  );
}

function Header({
  branchName,
  expiresAt,
}: {
  branchName?: string;
  expiresAt?: string | null;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-surface text-sm">
      <div className="flex items-center gap-2">
        <Monitor className="size-4 text-muted-foreground" />
        <span className="font-medium">Shared Preview</span>
        {branchName && (
          <span className="text-muted-foreground">— {branchName}</span>
        )}
      </div>
      {expiresAt && (
        <span className="text-xs text-muted-foreground">
          Expires: {formatDateTimeOrDash(expiresAt)}
        </span>
      )}
    </div>
  );
}
